import { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PaymentCoupons from "app/pages/Booking/Payment/PaymentCoupons/PaymentCoupons";
import { isAuthenticated, isOperatorMode } from "app/pages/Auth/authSelectors";
import {
	fetchUsableCoupons,
	fetchUsableCreditNotes,
} from "app/pages/Account/MyCoupons/couponActionCreators";
import {
	getInscriptionCreditCampaign,
	getOnlyNonApplicableCreditNotes,
	getOnlyNonApplicableCredits,
	getOrderedUnusableCreditNotes,
	getOrderedUnusableCredits,
	getOrderedUsableCreditNotes,
	getOrderedUsableCredits,
	getUsableCreditNotesTotal,
	getUsableCreditsTotal,
} from "app/pages/Account/MyCoupons/couponSelector";
import {
	CREDITS_INSCRIPTION_WAIT_DELAY,
	LOYALTY_CREDIT_TYPES,
	NAVIGATION_STATE_VALUES,
	PAYMENT_COUPON_TYPE,
} from "app/constants";
import { updateBooking } from "app/pages/Booking/bookingActionCreators";
import { sendTagOnCouponChange } from "app/utils/analytics";
import { useLocation } from "react-router-dom";
import usePrevious from "app/utils/hooks/usePrevious";

const PaymentCouponsContainer = props => {
	const {
		fetchUsableCoupons,
		fetchUsableCreditNotes,
		quotationCode,
		updateBooking,
		promotionCode,
		insuranceCode,
		couponType,
		isAuthenticated,
		enableCredits,
		enableCreditNotes,
		inscriptionCreditCampaign = {},
	} = props;

	const { state } = useLocation();

	const shouldAddDelayForSignupCredits =
		state === NAVIGATION_STATE_VALUES.REDIRECTED_FROM_BOOKING_AUTH &&
		inscriptionCreditCampaign.type === LOYALTY_CREDIT_TYPES.INSCRIPTION;

	const previousInsuranceCode = usePrevious(insuranceCode);

	useEffect(() => {
		if (isAuthenticated && (enableCredits || enableCreditNotes)) {
			fetchUsableCoupons(
				{
					connectivitySessionCode: quotationCode,
					isPromoCode: couponType === PAYMENT_COUPON_TYPE.PROMOCODE,
					promotionCode: promotionCode,
					insuranceCode,
				},
				{ delay: shouldAddDelayForSignupCredits ? CREDITS_INSCRIPTION_WAIT_DELAY : 0 }
			);
		}
	}, []);

	useEffect(() => {
		if (
			isAuthenticated &&
			previousInsuranceCode !== insuranceCode &&
			(enableCredits || enableCreditNotes)
		) {
			fetchUsableCoupons(
				{
					connectivitySessionCode: quotationCode,
					isPromoCode: couponType === PAYMENT_COUPON_TYPE.PROMOCODE,
					promotionCode: promotionCode,
					insuranceCode,
				},
				{ delay: 0 }
			);
		}
	}, [previousInsuranceCode, insuranceCode]);

	const onCouponChange = useCallback(
		value => {
			updateBooking({
				coupon: value,
			});

			fetchUsableCreditNotes({
				connectivitySessionCode: quotationCode,
				isPromoCode: value === PAYMENT_COUPON_TYPE.PROMOCODE,
				promotionCode: promotionCode,
				insuranceCode,
			});

			sendTagOnCouponChange(value);
		},
		[quotationCode, insuranceCode, promotionCode]
	);

	const forcePromoCodeSelection = useCallback(
		codePromo => {
			if (codePromo) {
				updateBooking({
					coupon: PAYMENT_COUPON_TYPE.PROMOCODE,
				});
			}

			if (isAuthenticated) {
				fetchUsableCreditNotes({
					connectivitySessionCode: quotationCode,
					isPromoCode: couponType === PAYMENT_COUPON_TYPE.PROMOCODE,
					promotionCode: codePromo,
					insuranceCode,
				});
			}
		},
		[couponType, insuranceCode, quotationCode]
	);

	return (
		<PaymentCoupons
			{...props}
			onChange={onCouponChange}
			onValidPromoCodeApplied={forcePromoCodeSelection}
			onPromoCodeFocus={forcePromoCodeSelection}
		/>
	);
};

PaymentCouponsContainer.propTypes = {
	field: PropTypes.object,
	usableCouponsStatus: PropTypes.string,
	fetchUsableCoupons: PropTypes.func,
	fetchUsableCreditNotes: PropTypes.func,
	updateBooking: PropTypes.func,
	totalUsableCredit: PropTypes.number,
	enablePromotionCode: PropTypes.bool,
	quotationCode: PropTypes.string,
	promotionCode: PropTypes.string,
	insuranceCode: PropTypes.string,
	couponType: PropTypes.string,
	isAuthenticated: PropTypes.bool,
	isStopover: PropTypes.bool,
	enableCredits: PropTypes.bool,
	enableCreditNotes: PropTypes.bool,
	inscriptionCreditCampaign: PropTypes.object,
};

const mapStateToProps = state => {
	const usableCouponsStatus = state.usableCoupons.status;
	const totalUsableCredit = getUsableCreditsTotal(state);
	const enablePromotionCode = isOperatorMode(state) || !state.partner.disablePromotionCode;
	return {
		enablePromotionCode,
		enableCredits: state.partner.enableCredits,
		enableCreditNotes: state.partner.enableCreditNotes,
		quotationCode: state.quotation.code,
		usableCredits: getOrderedUsableCredits(state),
		unusableCredits: getOrderedUnusableCredits(state),
		usableCreditNotes: getOrderedUsableCreditNotes(state),
		unusableCreditNotes: getOrderedUnusableCreditNotes(state),
		onlyNonApplicableCredits: getOnlyNonApplicableCredits(state),
		onlyNonApplicableCreditNotes: getOnlyNonApplicableCreditNotes(state),
		totalUsableCredit,
		totalUsableCreditNote: getUsableCreditNotesTotal(state),
		couponType: state.booking.coupon,
		usableCouponsStatus,
		promotionCode: state.promotion.promotionCode,
		promotionStatus: state.promotion.status,
		promotionMessage: state.promotion.message,
		insuranceCode: state.booking.insurance.code,
		isAuthenticated: isAuthenticated(state),
		promotionValue: state.promotion.promotionValue || 0,
		inscriptionCreditCampaign: getInscriptionCreditCampaign(state),
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			fetchUsableCoupons,
			fetchUsableCreditNotes,
			updateBooking,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(PaymentCouponsContainer);
